import React, { useEffect, useState } from 'react';
import { Stack, Form, Button } from 'react-bootstrap';
import { useAuth } from '../../api/auth/store';
import MpinInput from '../../components/Auth/MpinInput';
import { getLoginPasskeyAssertion, checkPasskeySupport } from '../../api/auth/passkey_actions';
import { AuthData } from '../../api/auth/actions';

const MFA_PIN_LENGTH = 6;
const CREDENTIAL_TYPE_MPIN = "mpin";
const CREDENTIAL_TYPE_PASSKEY = "passkey";

interface LoginMfaChallengeProps {
    error?: string;
    // Receives the result of the second-factor submission so the page can continue the login.
    onResult: (data: AuthData) => void;
}

const LoginMfaChallenge: React.FC<LoginMfaChallengeProps> = ({ error = "", onResult }) => {
    const { loginMfa, completeLoginMfa, clearLoginMfa, signingIn } = useAuth();
    const [passkeySupport] = useState(() => checkPasskeySupport());
    const [currentError, setCurrentError] = useState(error);

    const methods = loginMfa?.methods || [];
    const showMpin = methods.includes(CREDENTIAL_TYPE_MPIN);
    const showPasskey = methods.includes(CREDENTIAL_TYPE_PASSKEY) && passkeySupport.supported;

    const [authType, setAuthType] = useState<string>(CREDENTIAL_TYPE_MPIN);

    useEffect(() => {
        // Prefer passkey when available, otherwise fall back to MPIN.
        if (showPasskey) setAuthType(CREDENTIAL_TYPE_PASSKEY);
        else if (showMpin) setAuthType(CREDENTIAL_TYPE_MPIN);
    }, [showPasskey, showMpin]);

    useEffect(() => {
        if (error) setCurrentError(error);
    }, [error]);

    const submit = async (credentialType: string, credentialValue: string) => {
        setCurrentError("");
        try {
            const res = await completeLoginMfa(credentialType, credentialValue);
            if (res.Code !== "200") {
                setCurrentError(getMfaErrorMessage(res.Code));
            }
            onResult(res);
        } catch (err: any) {
            setCurrentError(err?.message || "Verification failed. Please try again.");
        }
    };

    const onPasskey = async () => {
        setCurrentError("");
        try {
            const assertion = await getLoginPasskeyAssertion(loginMfa?.username || "");
            await submit(CREDENTIAL_TYPE_PASSKEY, assertion);
        } catch (err: any) {
            setCurrentError(err?.message || "Passkey authentication failed.");
        }
    };

    const showOptions = showMpin && showPasskey;

    return (
        <div className="reauth-container" style={{ minHeight: '300px' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <h5 style={{ fontWeight: 'normal' }}>
                    Verify it's you{loginMfa?.display ? <>, <strong>{loginMfa.display}</strong></> : null}
                </h5>
                <p style={{ color: '#6c757d', margin: 0 }}>Complete multifactor authentication to continue.</p>
            </div>

            {showOptions && (
                <Form.Group className="mb-3" style={{ textAlign: 'center' }}>
                    {showPasskey && <Form.Check inline type="radio" label="Passkey" name="loginMfaType"
                        id="loginMfaPasskey" checked={authType === CREDENTIAL_TYPE_PASSKEY}
                        onChange={() => { setAuthType(CREDENTIAL_TYPE_PASSKEY); setCurrentError(''); }} />}
                    {showMpin && <Form.Check inline type="radio" label="MPIN" name="loginMfaType"
                        id="loginMfaMpin" checked={authType === CREDENTIAL_TYPE_MPIN}
                        onChange={() => { setAuthType(CREDENTIAL_TYPE_MPIN); setCurrentError(''); }} />}
                </Form.Group>
            )}

            <Stack direction="horizontal" gap={2} style={{ justifyContent: 'center' }}>
                {authType === CREDENTIAL_TYPE_MPIN && showMpin && (
                    <div style={{ flexGrow: 1 }}>
                        <MpinInput
                            enabled={!signingIn}
                            pinLength={MFA_PIN_LENGTH}
                            onChange={() => setCurrentError("")}
                            onFinish={(pin) => submit(CREDENTIAL_TYPE_MPIN, pin)}
                            label=""
                        />
                    </div>
                )}

                {authType === CREDENTIAL_TYPE_PASSKEY && showPasskey && (
                    <div style={{ flexGrow: 1, textAlign: 'center' }}>
                        <div style={{ padding: '20px', border: '2px dashed #007bff', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🔐</div>
                            <p style={{ margin: '0 0 10px 0', color: '#6c757d' }}>Use your device's passkey to continue</p>
                            <Button variant="primary" disabled={signingIn} onClick={onPasskey} style={{ minWidth: '120px' }}>
                                {signingIn ? 'Authenticating...' : 'Use Passkey'}
                            </Button>
                        </div>
                    </div>
                )}
            </Stack>

            {currentError && <p className="error-message" style={{ color: 'red', marginTop: '20px', textAlign: 'center' }}>{currentError}</p>}

            <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <button type="button" disabled={signingIn} onClick={() => clearLoginMfa()}
                    style={{ background: 'none', border: 'none', color: '#2196F3', cursor: signingIn ? 'not-allowed' : 'pointer', fontSize: '14px', textDecoration: 'underline' }}>
                    Cancel
                </button>
            </div>
        </div>
    );
};

// Maps the small set of MFA error codes the second-factor step can return to friendly text.
const getMfaErrorMessage = (code: string): string => {
    switch (code) {
        case "invalid_pin":
            return "Incorrect MPIN. Please try again.";
        case "invalid_credentials":
            return "Passkey verification failed. Please try again.";
        case "session_expired":
            return "Your login attempt expired. Please sign in again.";
        default:
            return "Verification failed. Please try again.";
    }
};

export default LoginMfaChallenge;
